import { Controller } from "stimulus";
import _ from "lodash";

const MA_RELATED_TYPES = ["medicare_advantage", "medicare_advantage_payers", "mapd", "pdp"];

// Pure function: Normalizes a select value, defaulting to first option when no blank option exists.
// Returns the value to use for the select.
// Exported for testing.
export function normalizeSelectValue(value, optionValues, hasBlankOption) {
  const isValidValue = value && optionValues.includes(value);

  if (isValidValue) {
    return value;
  }

  // Value is invalid (blank or non-existent ID)
  if (hasBlankOption) {
    return ""; // Use blank/"All" option
  } else if (optionValues.length > 0) {
    return optionValues[0]; // Default to first option
  }

  return "";
}

// Pure function: Translates MA-related payer types from the URL hash to match what the view shows.
// If the hash has any MA-related type (mapd, pdp, medicare_advantage, medicare_advantage_payers)
// but the view shows different MA types, translate to the view's types.
// Exported for testing.
export function translateMaPayerTypes(payerTypesInHash, availablePayerTypes) {
  // Check if hash has any MA-related type
  const hashHasMaType = payerTypesInHash.some(t => MA_RELATED_TYPES.includes(t));

  if (!hashHasMaType) return payerTypesInHash;

  // Find which MA-related types the view shows
  const viewMaTypes = availablePayerTypes.filter(t => MA_RELATED_TYPES.includes(t));

  if (viewMaTypes.length === 0) return payerTypesInHash;

  // Check if hash already has what the view shows
  const hashHasViewTypes = viewMaTypes.some(t => payerTypesInHash.includes(t));

  if (hashHasViewTypes) return payerTypesInHash;

  // Translate: add all MA types the view shows
  return [...payerTypesInHash, ...viewMaTypes];
}

export default class extends Controller {
  static targets = ["toggle", "select", "link"];

  connect() {
    const params = this.currentHashParams();

    // Translate MA-related payer types between display modes
    // All these types represent Medicare Advantage data, just displayed differently
    // Only translate if payer_types are actually in the hash (don't create empty array)
    if (params.payer_types) {
      const payerTypeToggles = this.toggleTargets.filter(t => t.name === "payer_types");
      const availablePayerTypes = payerTypeToggles.map(t => t.value);

      params.payer_types = translateMaPayerTypes(params.payer_types, availablePayerTypes);
    }

    this.toggleTargets.forEach(toggle => {
      const checkedValues = params[toggle.name];

      if (checkedValues) {
        toggle.checked = params[toggle.name].includes(toggle.value);
      }
    });

    this.selectTargets.forEach(select => {
      const hashValue = params[select.name]?.[0] || "";
      const optionValues = Array.from(select.options).map(o => o.value);
      const hasBlankOption = optionValues.includes("");

      select.value = normalizeSelectValue(hashValue, optionValues, hasBlankOption);
    });

    this.updateHash(); // Discards junk/irrelevant entries
  }

  reset(event) {
    const exclusions = event.target.dataset.breadcrumbsAsTogglesResetOnChange?.split(" ");
    const newHash = this.toggleTargets
      .filter(toggle => toggle.checked)
      .concat(this.selectTargets)
      .filter(t => !exclusions.includes(t.name))
      .map(toggle => `${toggle.name}=${toggle.value}`)
      .join("&");

    window.location.hash = newHash;
    window.location.reload();
  }

  updateHash() {
    const newHash = this.toggleTargets
      .filter(toggle => toggle.checked)
      .concat(this.selectTargets)
      .map(toggle => `${toggle.name}=${toggle.value}`)
      .join("&");

    window.location.hash = newHash;

    this.linkTargets.forEach(target => {
      const url = new URL(target.href);

      url.hash = newHash;
      target.href = url;
    });
  }

  currentHashParams() {
    // MA-related payer types that may need translation between modes
    const maRelatedTypes = ["medicare_advantage", "medicare_advantage_payers", "mapd", "pdp"];

    const paramPairs = window.location.hash
      .slice(1)
      .split("&")
      .filter(assignment => !!assignment)
      .map(assignment => assignment.split("="))
      .map(([key, value]) => [key, decodeURIComponent(value)])
      .filter(([key, value]) => {
        const toggleExists = this.toggleTargets.some(t => t.name === key && t.value === value);
        const selectExists = _.map(
          this.selectTargets.find(s => s.name === key)?.options,
          o => o.value === value,
        );

        // Preserve MA-related payer types for translation even if no matching toggle exists
        const isMaRelated = key === "payer_types" && maRelatedTypes.includes(value);

        return toggleExists || selectExists || isMaRelated;
      });

    const params = {};

    paramPairs.forEach(([key, value]) => {
      if (!params[key]) params[key] = [];
      params[key].push(value);
    });

    return params;
  }
}
